'use strict';

import React, { useState, useEffect, useCallback } from 'react';
import { connect } from 'react-redux';
import Modal from 'react-modal';
import { offset } from '@floating-ui/react-dom';
import cn from 'classnames/bind';

import { appSelectors, appOperations } from '@/state/domains/app';
import { usersSelectors, usersOperations } from '@/state/domains/users';
import { placesSelectors, placesOperations } from '@/state/domains/places';

import {
	hasError,
	triggerErrors,
	triggerServerErrors
} from '@/state/utilities/forms';

import { workWeekOpts, roleOpts, formConfig } from '@/state/domains/users/utils';

import Tooltip from '@/views/common/Tooltip';
import SimpleSelect from '@/views/common/SimpleSelect';
import MultiSelect from '@/views/common/MultiSelect';
import RoleSelect from '@/views/common/RoleSelect';

import s from './styles.scss';
import m from '../styles.scss';

const sx = cn.bind(s);

export default connect(
	(state, props) => ({
		modals: appSelectors.getModalStates(state, props),
		modalProps: appSelectors.getModalProps(state, props),
	}),
	{
		setModalClose: appOperations.setModalClose,
		postUpdateUser: usersOperations.postUpdateUser,
		getOffices: placesOperations.getOffices,
	}
)(
	({
		modalProps,
		modals,
		setModalClose,
		postUpdateUser,
		getOffices,
	}) => {

		const { user, updateUser } = modalProps;

		if (!user) {
			return <div />;
		}

		useEffect(
			() => {
				const p1 = getOffices();

				Promise.all([ p1 ]).then(
					([ r1 ]) => {
						if (r1.success) {

							setOffices(r1.data.offices.map(o => ({
								label: o.name,
								value: o._id,
							})));

							if (user.office) {
								const office = r1.data.offices.find(
									o => o._id === user.office
								);
								if (office) {
									setOffice({ label: office.name, value: office._id });
								}
							}
						}
					}
				);
			},
			[]
		);

		const [ firstName, setFirstName ] = useState(user.firstName);
		const [ lastName, setLastName ] = useState(user.lastName);
		const [ username, setUsername ] = useState(user.username);
		const [ email, setEmail ] = useState(user.email);
		const [ passphrase, setPassphrase ] = useState('');
		const [ confirmPassphrase, setConfirmPassphrase ] = useState('');
		const [ roles, setRoles ] = useState(roleOpts.reduce(
			(roles, opt) => {
				if (user.roles.includes(opt.value)) {
					return [ ...roles, opt ];
				}
				return roles;
			},
			[]
		));
		const [ availableRoles, setAvailableRoles ] = useState(roleOpts);
		const [ offices, setOffices ] = useState([]);
		const [ office, setOffice ] = useState({});
		const [ workWeek, setWorkWeek ] = useState({
			label: user.settings.workWeek,
			value: user.settings.workWeek,
		});

		const [ firstNameE, setFirstNameE ] = useState(false);
		const [ lastNameE, setLastNameE ] = useState(false);
		const [ usernameE, setUsernameE ] = useState(false);
		const [ emailE, setEmailE ] = useState(false);
		const [ passphraseE, setPassphraseE ] = useState(false);
		const [ confirmPassphraseE, setConfirmPassphraseE ] = useState(false);
		const [ officeE, setOfficeE ] = useState(false);
		const [ rolesE, setRolesE ] = useState(false);
		const [ workWeekE, setWorkWeekE ] = useState(false);

		const onFirstNameChange = e => {
			setFirstName(e.target.value);
			setFirstNameE(false);
		};

		const onLastNameChange = e => {
			setLastName(e.target.value);
			setLastNameE(false);
		};

		const onUsernameChange = e => {
			setUsername(e.target.value.trim());
			setUsernameE(false);
		};

		const onEmailChange = e => {
			setEmail(e.target.value.trim());
			setEmailE(false);
		};

		const onPassphraseChange = e => {
			setPassphrase(e.target.value);
			setPassphraseE(false);
		};

		const onConfirmPassphraseChange = e => {
			setConfirmPassphrase(e.target.value);
			setConfirmPassphraseE(false);
		};

		const onOfficeChange = value => {
			setOffice(value);
			setOfficeE(false);
		};

		const onRolesChange = (e, o, i) => {

			const newValue = [
				...roles,
				o,
			];
			const r = roles.find(r => r.value === o.value);
			
			let _roles;

			if (r) {
				_roles = roles.filter(r => r.value !== o.value);
			} else {
				_roles = [ ...roles, o ];
			}

			setRoles(_roles);
			setRolesE(false);
		};

		const onWorkWeekChange = opt => setWorkWeek(opt);

		const close = () => {
			setModalClose('editUser')
		};

		const makeUIReporter = errorFn => ({
			affirmative: messages => errorFn({
				error: true,
				message: messages[ 0 ].message,
			}),
			negative: () => errorFn({ error: false, message: '' }),
		});

		const errorReporter = {
			firstName: makeUIReporter(setFirstNameE),
			lastName: makeUIReporter(setLastNameE),
			username: makeUIReporter(setUsernameE),
			passphrase: makeUIReporter(setPassphraseE),
			confirmPassphrase: makeUIReporter(setConfirmPassphraseE),
			office: makeUIReporter(setOfficeE),
			roles: makeUIReporter(setRolesE),
			workWeek: makeUIReporter(setWorkWeekE),
		};

		const prepareData = _data => {

			const data = { ..._data };

			if ('office' in _data) {
				data.office = _data.office.value;
			}

			if ('workWeek' in _data) {
				data.workWeek = _data.workWeek.value;
			}

			if ('roles' in _data) {
				data.roles = _data.roles.map(o => o.value);
			}

			return data;
		};

		const submit = useCallback(
			e => {

				e.preventDefault();

				let data = {
					firstName,
					lastName,
					username,
					email,
					office,
					roles,
					workWeek,
				};

				let props = Object.keys(data);

				if (passphrase || confirmPassphrase) {
					data.passphrase = passphrase;
					data.confirmPassphrase = confirmPassphrase;
				}

				data = prepareData(data);
				data = formConfig.sanitize(formConfig.fields, data);

				const validationReport = formConfig.validate(formConfig.fields, data);

				if (hasError(validationReport)) {
					triggerErrors(errorReporter, validationReport);

				} else {
					postUpdateUser(user._id, data).then(r => {
						if (r.success) {
							updateUser(r.data.user);
							close();
						} else {
							triggerServerErrors(errorReporter, r.data.errors);
						}
					}).catch(e => {
						triggerServerErrors(errorReporter, e.data.errors);
					});
				}
			},
			[
				firstName,
				lastName,
				username,
				passphrase,
				confirmPassphrase,
				office,
				roles,
				workWeek,
			]
		);

		const tooltipInit = {
			placement: 'right',
			middleware: ({ arrowRef, arrow, offset }) => [
				offset(4),
				arrow({ element: arrowRef }),
			],
		};

		return (
			<Modal
				appElement={ document.getElementById('root') }
				overlayClassName={ m.modalOverlay }
				isOpen={ modals.editUser }
				contentLabel='Edit User'
				shouldCloseOnOverlayClick={ true }
				shouldCloseOnEsc={ true }
				onRequestClose={ close }
				className={ m.modal }
			>
				<div className={ s.root }>
					<header className={ s.header }>
						Edit User
					</header>
					<form className={ s.form } >
						<div className={ s.formRow }>
							<div className={ s.formCol }>
								<label className={ s.label }>First Name</label>
								<Tooltip
									init={ tooltipInit }
									show={ firstNameE && firstNameE.error }
									className={ s.errorTooltip }
									arrowClassName={ s.arrow }
									arrowStyle={{
										left: -3,
									}}
									content={ firstNameE.message }
								>
									<span className={ s.formCont }>
										<input
											type='text'
											autoFocus={ true }
											className={ sx('input', { error: firstNameE }) }
											onChange={ onFirstNameChange }
											value={ firstName }
										/>
									</span>
								</Tooltip>
							</div>
							<div className={ s.formCol }>
								<label className={ s.label }>Last Name</label>
								<Tooltip
									init={ tooltipInit }
									show={ lastNameE && lastNameE.error }
									className={ s.errorTooltip }
									arrowClassName={ s.arrow }
									arrowStyle={{
										left: -3,
									}}
									content={ lastNameE.message }
								>
									<span className={ s.formCont }>
										<input
											type='text'
											className={ s.input }
											className={ sx('input', { error: lastNameE }) }
											onChange={ onLastNameChange }
											value={ lastName }
										/>
									</span>
								</Tooltip>
							</div>
						</div>
						<div className={ s.formRow }>
							<div className={ s.formCol }>
								<label className={ s.label }>Username</label>
								<Tooltip
									init={ tooltipInit }
									show={ usernameE && usernameE.error }
									className={ s.errorTooltip }
									arrowClassName={ s.arrow }
									arrowStyle={{
										left: -3,
									}}
									content={ usernameE.message }
								>
									<span className={ s.formCont }>
										<input
											type='text'
											className={ s.input }
											className={ sx('input', { error: usernameE }) }
											onChange={ onUsernameChange }
											value={ username }
										/>
									</span>
								</Tooltip>
							</div>
							<div className={ s.formCol }>
								<label className={ s.label }>Email</label>
								<div className={ s.formCont }>
									<input
										type='text'
										className={ s.input }
										className={ sx('input', { error: emailE }) }
										onChange={ onEmailChange }
										value={ email }
									/>
								</div>
							</div>
						</div>
						<div className={ s.formRow }>
							<div className={ s.formCol }>
								<label className={ s.label }>Passphrase</label>
								<Tooltip
									init={ tooltipInit }
									show={ passphraseE && passphraseE.error }
									className={ s.errorTooltip }
									arrowClassName={ s.arrow }
									arrowStyle={{
										left: -3,
									}}
									content={ passphraseE.message }
								>
									<span className={ s.formCont }>
										<input
											type='password'
											className={ s.input }
											className={ sx('input', { error: passphraseE }) }
											onChange={ onPassphraseChange }
											value={ passphrase }
										/>
									</span>
								</Tooltip>
							</div>
							<div className={ s.formCol }>
								<label className={ s.label }>Confirm Passphrase</label>
								<Tooltip
									init={ tooltipInit }
									show={ confirmPassphraseE && confirmPassphraseE.error }
									className={ s.errorTooltip }
									arrowClassName={ s.arrow }
									arrowStyle={{
										left: -3,
									}}
									content={ confirmPassphraseE.message }
								>
									<span className={ s.formCont }>
										<input
											type='password'
											className={ s.input }
											className={ sx('input', { error: confirmPassphraseE }) }
											onChange={ onConfirmPassphraseChange }
											value={ confirmPassphrase }
										/>
									</span>
								</Tooltip>
							</div>
						</div>
						<div className={ s.formRow }>
							<div className={ sx('formCol', 'office') }>
								<label className={ s.label }>Office</label>
								<Tooltip
									init={ tooltipInit }
									show={ officeE && officeE.error }
									className={ s.errorTooltip }
									arrowClassName={ s.arrow }
									arrowStyle={{
										left: -3,
									}}
									content={ officeE.message }
								>
									<SimpleSelect
										value={ office }
										options={ offices }
										classNames={ { valueCont: sx({ error: officeE })} }
										onChange={ onOfficeChange }
									/>
								</Tooltip>
							</div>
							<div className={ sx('formCol', 'roles') }>
								<label className={ s.label }>Roles</label>
								<Tooltip
									init={ tooltipInit }
									show={ rolesE && rolesE.error }
									className={ s.errorTooltip }
									arrowClassName={ s.arrow }
									arrowStyle={{
										left: -3,
									}}
									content={ rolesE.message }
								>
									<span className={ s.formCont }>
										<RoleSelect
											values={ roles }
											options={ availableRoles }
											classNames={ { valueCont: sx({ error: rolesE })} }
											onChange={ onRolesChange }j
											onRemoveItem={
												(e, o, i) => {
													setRoles([
														...roles.slice(0, i),
														...roles.slice(i + 1),
													]);
													setAvailableRoles([
														...availableRoles,
														o,
													]);
												}
											}
										/>
									</span>
								</Tooltip>
							</div>
							<div className={ sx('formCol', 'workWeek') }>
								<label className={ s.label }>Work Week</label>
								<Tooltip
									init={ tooltipInit }
									show={ workWeekE && workWeekE.error }
									className={ s.errorTooltip }
									arrowClassName={ s.arrow }
									arrowStyle={{
										left: -3,
									}}
									content={ workWeekE.message }
								>
									<span className={ s.formCont }>
										<SimpleSelect
											value={ workWeek }
											options={
												workWeekOpts.map(
													o => ({ label: o, value: o })
												)
											}
											onChange={ opt => setWorkWeek(opt) }
										/>
									</span>
								</Tooltip>
							</div>
						</div>
					</form>
					<button
						type='submit'
						className={ s.submit }
						onClick={ submit }
					>
						Update
					</button>
				</div>
			</Modal>
		);
	}
);
